import { useCallback, useState } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSRequest, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppDispatch, CollectionFilesyncAPI, useSendRequestMutation, DriveRequest, ErrorStackItem, Message, getEnumKey, MessageHandlerDisplayType, DriveWatchedErrorMessages,
  EntryItemBase, GenericEntryEntity, DriveStarredErrorMessages, EntryItem
} from "src/common";
import { uiActions, driveActions, DriveCallbackAsyncFunction } from "src/store";
import _ from "lodash";

type StarState = {
    name: string;
    is_starred: boolean;
}
// Description: Hook to set starred state for nodes in the drive
// It handles starring and unstarring nodes, bulk actions, and error handling
// NOTE: If you attempt to add nodes that are deleted or inaccessible, they will be removed within the same transaction (meaning you'll never see those).
// If you attempt to remove nodes that you don't have as favorite, there's no effect (they're already not there).
// To keep it more concrete, if you try to favorite these nodes: [A, B, C*, D, E, F+] , where * means deleted and + means inaccessible, the result will be that only [A, B, D, E] will survive the cleanup at the end of the operation.
export function useStarNode(current_account: Account) {
    const dispatch = useAppDispatch();
    const [sendRequest] = useSendRequestMutation();
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [star, setStar] = useState<StarState>();
    const [warning, setWarning] = useState<boolean>();
    const [bulk_success, setBulkSuccess] = useState<boolean>(false);

    // Description: Map the nodes to the CollectionNode object
    function mapToCollectionNode(collection_id: string, id: string ): FSRequest.CollectionNode {
        const collection_node = new FSRequest.CollectionNode();
        collection_node.setCollectionId(collection_id);
        collection_node.setNodeId(id);
        return collection_node;
    }

    // Description: Star Nodes (Bulk action) (see comment on top of the file for more details about errors)
    async function handleStarNode(nodes: FSRequest.CollectionNode[], is_bulk_star: boolean = false) {
        const drive_request = await CollectionFilesyncAPI.starItem(current_account, nodes);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                is_bulk_star ? setBulkSuccess(true) : setSuccess(true);
            } else {
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveStarredErrorMessages.error_setting_node_star }, message,
                    MessageHandlerDisplayType.toastr);
            }
        }
        handleSendRequest(drive_request, callback, DriveStarredErrorMessages.error_setting_node_star);
    }

    // Description: Unstar Nodes (Bulk action) (see comment on top of the file for more details about errors)
    // `success_star` tracks prior star request outcome if in bulk toggle
    async function handleUnstarNode(nodes: FSRequest.CollectionNode[]) {
        const drive_request = await CollectionFilesyncAPI.unstarItem(current_account, nodes);
        async function callback(message: FSMessage) {
            if (message.getStatus() === FSStatus.OK) {
                setSuccess(true);
            } else {
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveWatchedErrorMessages.error_setting_node_watch }, message,
                    MessageHandlerDisplayType.toastr);
            }
        }
        handleSendRequest(drive_request, callback, DriveStarredErrorMessages.error_setting_node_star);
    }

    // Description: Remove ALL Starred items
    async function handleClearAllStarred() {
        const drive_request = await CollectionFilesyncAPI.clearAllStarred(current_account);
        async function callback(message: FSMessage) {
            (message.getStatus() === FSStatus.OK) ?
                setSuccess(true) :
                handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_message: DriveStarredErrorMessages.error_setting_node_star }, message,
                    MessageHandlerDisplayType.toastr);
        }
        handleSendRequest(drive_request, callback, DriveStarredErrorMessages.error_setting_node_star);
    }

    // -----------------------------
    // HOOK General
    // -----------------------------
    // Description: Sends Request and handles error when the request object is null, pass it custom stack message
    function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveWatchedErrorMessages.error_sending_request) {
        if (!!request) {
            request.setCallback(callback);
            sendRequest(request);
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.default, { stack_error, stack_message: DriveWatchedErrorMessages.error_sending_request });
        }
    }
    // Description:  send error to store
    function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
        const stack = new ErrorStackItem("[useSetStarredState Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
        setError(true);
    }

    // -----------------------------
    // Callbacks
    // -----------------------------

    // Description: Call hook for starring / unstarring nodes and descendants
    const setNodeStarredState = useCallback((entry: EntryItemBase, _star: boolean) => {
        const { collection_id, id } = entry;
        dispatch(driveActions.setLoading(true));
        setStar({ name: entry.name || "Your selected item", is_starred: _star });
        if (_star) {
            handleStarNode([mapToCollectionNode(collection_id, id)]);
        } else {
            handleUnstarNode([mapToCollectionNode(entry.collection_id, entry.id)]);
        }
    }, []);

     // Description: Call hook for starring / unstarring nodes and descendants
    const setBulkNodeStarredState = useCallback((entries: EntryItem[]) => {
        if (!entries || entries.length === 0) return;
        const entries_to_star = entries.filter((entry) => !entry.starred).map((entry: EntryItem) => mapToCollectionNode(entry.collection_id, entry.id));
        entries_to_star.length > 0 ? handleStarNode(entries_to_star, true) : setWarning(true);
    }, []);


    // Description: Call hook for unstarring nodes
    const setBulkUnStarState = useCallback((entries: GenericEntryEntity[]) => {
        dispatch(driveActions.setLoading(true));
        const nodes = _.map(entries, (entry: GenericEntryEntity) => {
            return mapToCollectionNode(entry.collection_id, entry.id);
        });
        nodes.length === 1 && setStar({ name: entries[0].name || "Your selected item", is_starred: false });
        handleUnstarNode(nodes);
    }, []);

    // Description: Call hook for unstarring all nodes
    const clearAllStarredNodes = useCallback(() => {
        handleClearAllStarred();
    }, []);

    // Description: Reset all from component
    const reset = useCallback(() => {
        setSuccess(false);
        setError(false);
        setBulkSuccess(false);
        setStar(undefined);
        setWarning(false);
    }, []);

    return {
        setNodeStarredState,
        setBulkUnStarState,
        clearAllStarredNodes,
        setBulkNodeStarredState,
        reset,
        success,
        error,
        bulk_success,
        star,
        warning

    };
}
